/**
 * G5 (RC-6, AC-GP-011) — a save failure speaks a human sentence.
 *
 * A generation that dies while WRITING its blocks used to leave the owner with
 * «Необработанная ошибка CUSTOM_SUBTYPE_PERSISTENCE_INVALID: …» and a Retry that
 * did nothing. This module is the single place that turns such a raw failure
 * into a sentence, so both sides tell the owner the same thing:
 *
 *   - the builder executors compose the task's `error` at the moment of failure
 *     (they also know how many blocks the run had already saved), and
 *   - the task dialog re-derives the sentence for a LEGACY task whose document
 *     predates that mapping and carries only the raw text (Ruling GP-R11).
 *
 * Pure, dependency-free: builder-service imports it RELATIVELY (Guard 4) and the
 * frontend through `@modules/shared-types`.
 */

/** The three persistence failures a generation run can end on (Ruling GP-R4 / G2). */
export const STRATEGY_PERSISTENCE_ERROR_CODES = [
  'PERSISTENCE_PREFLIGHT_FAILED',
  'CUSTOM_SUBTYPE_PERSISTENCE_INVALID',
  'BLOCK_SCREEN_CONTRACT_PERSISTENCE_INVALID',
] as const;

export type StrategyPersistenceErrorCode = typeof STRATEGY_PERSISTENCE_ERROR_CODES[number];

export interface ParsedStrategyPersistenceFailure {
  /**
   * The code that says WHAT was wrong. `PERSISTENCE_PREFLIGHT_FAILED` is a
   * wrapper that says WHERE, so a wrapped message reports the inner code.
   */
  code: StrategyPersistenceErrorCode;
  /** Present only when the failure named its block (the preflight wrapper does). */
  blockId?: string;
  blockName?: string;
  /** The message without its code prefix and without the block clause. */
  reason: string;
}

/** What the run had actually saved when it failed — `StrategyGenerationOutcome['counts']`. */
export interface SavedBlockCounts {
  blocks: number;
  accepted: number;
  failed: number;
  invalid: number;
  pending: number;
  unwritten: number;
}

/** Verbatim wrapper format bound by Ruling GP-R4: `PERSISTENCE_PREFLIGHT_FAILED: block <id> «<name>»: <reason>`. */
const PREFLIGHT_BLOCK_PATTERN = /^block\s+(\S*)\s+«([^»]*)»:\s*([\s\S]*)$/;

/**
 * Fix round 1 (M10): wrappers the pipeline itself puts in front of a persistence
 * failure before it lands on a document. Only these EXACT, code-owned prefixes are
 * stripped — a code mentioned inside an arbitrary sentence is still not a
 * classification (`strategyGenerationPersistence.recordScreenWriteFailure` →
 * `screen write failed: <message>`).
 */
const KNOWN_WRAPPER_PREFIXES = ['screen write failed: '] as const;

function leadingCode(message: string): StrategyPersistenceErrorCode | undefined {
  return STRATEGY_PERSISTENCE_ERROR_CODES.find((code) => message.startsWith(`${code}:`));
}

function stripKnownWrapper(message: string): string {
  for (const prefix of KNOWN_WRAPPER_PREFIXES) {
    if (message.startsWith(prefix)) return message.slice(prefix.length).trim();
  }
  return message;
}

/**
 * The parsed failure, or `undefined` when the message is not one. The code must
 * LEAD the message — a code mentioned inside a longer sentence is not a
 * classification, and guessing would mislabel unrelated failures.
 */
export function parseStrategyPersistenceFailure(
  message: string | undefined | null,
): ParsedStrategyPersistenceFailure | undefined {
  if (typeof message !== 'string') return undefined;
  const text = stripKnownWrapper(message.trim());
  const code = leadingCode(text);
  if (!code) return undefined;

  const body = text.slice(code.length + 1).trim();
  if (code !== 'PERSISTENCE_PREFLIGHT_FAILED') return { code, reason: body };

  const match = PREFLIGHT_BLOCK_PATTERN.exec(body);
  if (!match) return { code, reason: body };

  const [, blockId, blockName, wrapped] = match;
  const inner = leadingCode(wrapped.trim());
  return {
    code: inner ?? code,
    ...(blockId ? { blockId } : {}),
    ...(blockName ? { blockName } : {}),
    reason: inner ? wrapped.trim().slice(inner.length + 1).trim() : wrapped.trim(),
  };
}

/** `2 of 6 planned block(s) were saved (1 failed, 1 invalid, 2 not generated)`. */
export function describeSavedBlockCounts(counts: SavedBlockCounts): string {
  return `${counts.accepted} of ${counts.blocks} planned block(s) were saved `
    + `(${counts.failed} failed, ${counts.invalid} invalid, ${counts.pending + counts.unwritten} not generated)`;
}

/**
 * Ruling GP-R27: an exception exit that already saved blocks never claims an
 * unchanged strategy — the thrown message keeps its wording and gains the counts.
 */
export function appendSavedBlockCounts(message: string, counts: SavedBlockCounts): string {
  return `${message} — ${describeSavedBlockCounts(counts)}; the rest of the strategy was not changed.`;
}

/**
 * What failed, with no claim about what survived — for a caller that cannot know
 * (fix round 1, I3: a CHILD block-screen task ends here; only the run's own
 * persistence module knows what the strategy kept).
 */
export function strategyPersistenceFailureHeadline(failure: ParsedStrategyPersistenceFailure): string {
  const blockClause = failure.blockName ? ` on block «${failure.blockName}»` : '';
  const reason = failure.reason || failure.code;
  const head = `Generation finished, but saving failed${blockClause}: ${reason}`;
  return /[.!?]$/.test(head) ? head : `${head}.`;
}

/**
 * The sentence AC-GP-011 binds. The closing clause is the truthful one for what
 * the run actually wrote: nothing saved → «The strategy was not changed.»;
 * blocks already saved → what was kept.
 */
export function describeStrategyPersistenceFailure(
  failure: ParsedStrategyPersistenceFailure,
  counts?: SavedBlockCounts,
): string {
  const headline = strategyPersistenceFailureHeadline(failure);
  return counts && counts.accepted > 0
    ? `${headline} ${describeSavedBlockCounts(counts)}; the rest of the strategy was not changed.`
    : `${headline} The strategy was not changed.`;
}
